import React, { useState, useEffect, useContext  } from 'react'; 
import { UserContext } from '../../lib/usercontext';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ClientModal from '../common/modals/client_modal';
import MenuDropdown from '../common/dropdowns/dropdown_menu';
import MasterLayout from '../layouts/masterlayout';
import ErrorMessage from '../common/errormessage';
import SuccessMessage from '../common/successmessage';
import { getHeaders, handleErrorResponse, handleResponse } from '../../lib/requests';
import { useNavigate } from 'react-router-dom';
import GeneralButton from '../common/button_general';
import Spinner from '../common/spinner';

function CustomerForm() {
  const { user } = useContext(UserContext);
  const role = user.userGroups ? user.userGroups[0] : 'Customer';
  const isAdmin = role === 'Admin'; 

  const navigate = useNavigate();
  const { customerId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);

  const handleBack = () => navigate('/clients');

  const [formData, setFormData] = useState({
    user_id: '',
    client_id: '',
    facility_id: '',
    provider_id: '',
    address_id: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    title: '',
    suffix: '',
    client_number: '',
    dietary_restriction_type: '',
    default_menu_id: '',
    default_order_1_id: null,
    default_order_2_id: null,
    default_order_3_id: null,
    default_order_4_id: null,
    reference_id: '',
    status: '',
    internal_notes: '',
    provider_notes: '',
    special_prep: '',
    delivery_schedule: '',
    delivery_days: '' // Changed from an array to a single value
  });

  const [selectedClient, setSelectedClient] = useState({ client_id: null, name: '' });
  const [menus, setMenus] = useState([]);
  const [isMenusLoading, setIsMenusLoading] = useState(false);

  const titleOptions = [
    { label: 'Mr.', value: 'Mr.' },
    { label: 'Mrs.', value: 'Mrs.' },
    { label: 'Ms.', value: 'Ms.' },
    { label: 'Dr.', value: 'Dr.' }
  ];

  const suffixOptions = [
    { label: 'Select Suffix', value: '' },
    { label: 'Jr.', value: 'Jr.' },
    { label: 'Sr.', value: 'Sr.' },
    { label: 'II', value: 'II' },
    { label: 'III', value: 'III' }
  ];

  const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'On Hold', value: 'on_hold' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Disenrolled', value: 'disenrolled' },
    { label: 'Deceased', value: 'deceased' },
  ];

  const specialPrepOptions = [
    { label: 'None', value: 'none' },
    { label: 'Chopped', value: 'chopped' },
    { label: 'Ground', value: 'ground' },
    { label: 'Pureed', value: 'pureed' },
  ];

  const deliveryDaysOptions = [
    { label: 'Sunday', value: 0 },
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 },
  ];

  const deliveryScheduleOptions = [
    { label: 'Weekly', value: 'weekly' },
    { label: 'Biweekly', value: 'biweekly' },
    { label: 'Monthly', value: 'monthly' },
  ];

  const fieldsSchema = [
    { name: 'first_name', type: 'text', required: true, placeholder: 'First Name' },
    { name: 'middle_name', type: 'text', required: false, placeholder: 'Middle Name' },
    { name: 'last_name', type: 'text', required: true, placeholder: 'Last Name' },
    { name: 'client_number', type: 'text', required: false, placeholder: 'Client Number' },
  ];

  const otherFieldsSchema = [
    { name: 'meal_count', type: 'number', required: true, placeholder: 'Meal Count' },
    { name: 'internal_route', type: 'text', required: true, placeholder: 'Provider Route' },
  ];

  const renderInput = (field) => (
    <div key={field.name}>
      <label htmlFor={field.name} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
        {field.placeholder}
      </label>
      <input
        type={field.type}
        id={field.name}
        name={field.name}
        value={formData[field.name]}
        onChange={handleChange}
        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
        placeholder={field.placeholder}
        required={field.required}
      />
    </div>
  );

  const renderDropdown = (options, name, label, isRequired = true) => (
    <div>
      <label htmlFor={name} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
        {label}
      </label>
      <select
        id={name}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
        {...(isRequired ? { required: true } : {})}
      >
        <option value="">{`Select ${label}`}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        setIsMenusLoading(true);
        const apiToken = localStorage.getItem('accessToken');
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await axios.get(`${apiUrl}/menus`, {
          headers: getHeaders(apiToken),
        });
        setMenus(response.data.data);
      } catch (error) {
        console.error('Error fetching menus', error);
      } finally {
        setIsMenusLoading(false);
      }
    };

    fetchMenus();
  }, []);

  useEffect(() => {
    if (!customerId) return;
    const fetchCustomerData = async () => {
      setIsLoading(true);
      try {
        const authorizationToken = localStorage.getItem('accessToken');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/customers/${customerId}`, {
          headers: getHeaders(authorizationToken)
        });
        const responseData = response.data.data;
        const status = responseData.status || 'active';
        const special_prep = responseData.special_prep || 'none';
        const meal_count = responseData.meal_count || 0;
        const internal_route = responseData.internal_route || '';
        setSelectedClient({ client_id: responseData.client_id, name: responseData.client_name });
        setFormData(prevState => ({
          ...prevState,
          ...responseData,
          status: status,
          special_prep: special_prep,
          meal_count: meal_count,
          internal_route: internal_route,
          provider_notes: responseData.provider_notes || '',
        }));
        setShowError(false);
      } catch (error) {
        handleErrorResponse(error, navigate, setShowError, setErrorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomerData();
  }, [customerId]);

  const handleChange = e => {
    setFormData(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
  };

  const resetForm = () => {
    setShowError(false);
    setShowSuccess(false);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleClientSelect = (client) => {
    setFormData(prevState => ({ ...prevState, client_id: client.client_id }));
    setSelectedClient(client);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCustomerModalOpen) return;
    resetForm();
    if (!formData.default_menu_id) {
      setErrorMessage('Default Menu is required');
      setShowError(true);
      return;
    }
    const apiToken = localStorage.getItem('accessToken');
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiToken || !apiUrl) {
      setErrorMessage('Configuration error');
      setShowError(true);
      console.error('API token or URL is missing');
      return;
    }
    try {
      const method = customerId ? 'put' : 'post';
      const transformedFormData = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => [key, value === '' ? null : value])
      );
      const response = await axios({
        method,
        url: `${apiUrl}/customers${customerId ? `/${customerId}` : ''}`,
        data: transformedFormData,
        headers: getHeaders(apiToken),
      });
      handleResponse(
        response,
        "customer",
        setShowSuccess,
        setSuccessMessage,
        setShowError,
        setErrorMessage,
        navigate,
        '/clients/:id'
      );
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    }
  };

  const pageTitle = `${customerId ? 'Edit' : 'Create'} Client ${formData.first_name} ${formData.last_name}${formData.reference_id ? ` (${formData.reference_id})` : ''}`;

  return (
    <MasterLayout pageTitle={pageTitle}>
      {showError && <ErrorMessage message={errorMessage} />}
      {showSuccess && <SuccessMessage message={successMessage} setShowSuccess={setShowSuccess} />}
      {isLoading ? <Spinner /> : (
        <form onSubmit={handleSubmit} className="p-4">
          <div className="grid gap-6 mb-6 md:grid-cols-6">
            {fieldsSchema.map(field => renderInput(field))}
            {renderDropdown(titleOptions, 'title', 'Title', false)}
            {renderDropdown(suffixOptions, 'suffix', 'Suffix', false)}
          </div>
          <div className="grid gap-6 mb-6 md:grid-cols-2">
            {otherFieldsSchema.map(field => renderInput(field))}
          </div>
          <div className="grid gap-6 mb-2 md:grid-cols-4">
            {renderDropdown(statusOptions, 'status', 'Status')}
            <div>
              <ClientModal
                isOpen={isCustomerModalOpen}
                setIsOpen={setIsCustomerModalOpen}
                selectClient={handleClientSelect}
                value={selectedClient}
              />
            </div>
            {renderDropdown(specialPrepOptions, 'special_prep', 'Mechanically Altered')}
            {isMenusLoading ? (
              <Spinner />
            ) : (
              <div className="mb-4">
                <MenuDropdown
                  menus={menus}
                  label="Default Menu"
                  onSelect={(menu) =>
                    setFormData(prev => ({
                      ...prev,
                      default_menu_id: menu.menu_id,
                    }))
                  }
                  selectedMenu={{
                    menu_id: formData.default_menu_id,
                    name: menus.find(m => m.menu_id === formData.default_menu_id)?.name || '',
                  }}
                />
              </div>
            )}
          </div>
          <div className="grid gap-6 mb-2 md:grid-cols-2">
            <div className="grid grid-cols-2 gap-6">
              <div>{renderDropdown(deliveryScheduleOptions, 'delivery_schedule', 'Delivery Schedule')}</div>
              <div>{renderDropdown(deliveryDaysOptions, 'delivery_days', 'Delivery Day')}</div>
            </div>
            {customerId && (
              <div className="grid gap-4 grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-900">Create Date</label>
                  <div>{formData.create_date ? new Date(formData.create_date).toLocaleDateString() : ''}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900">Modified Date</label>
                  <div>{formData.modified_date ? new Date(formData.modified_date).toLocaleString() : ''}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900">Last Order Date</label>
                  <div>
                    {formData.last_order_date ? (
                      <a href={`/orders/${formData.last_order_id}`} target="_blank" rel="noopener noreferrer">
                        {new Date(formData.last_order_date).toLocaleDateString()}
                      </a>
                    ) : 'N/A'}
                  </div>
                </div>
              </div>
            )}
          </div>
          {isAdmin && (<div className="grid mb-6 md:grid-cols-1">
            <label htmlFor="internal_notes" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Internal Notes</label>
            <textarea
              id="internal_notes"
              name="internal_notes"
              value={formData.internal_notes}
              onChange={handleChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Enter any internal notes here"
            />
          </div> )}
          <div className="grid mb-6 md:grid-cols-1">
            <label htmlFor="provider_notes" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Provider Notes</label>
            <textarea
              id="provider_notes"
              name="provider_notes"
              value={formData.provider_notes}
              onChange={handleChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Enter any provider notes here"
            />
          </div>
          <div className="flex justify-center mt-6">
            <GeneralButton type="submit" buttonType="primary">
              {customerId ? 'Update Client' : 'Create Client'}
            </GeneralButton>
            <GeneralButton type="button" buttonType="secondary" onClick={handleBack}>
              Back to Client List
            </GeneralButton>
          </div>
          {customerId && (
            <div className="flex justify-center gap-4 mt-6 flex-wrap">
              {[1, 2, 3, 4].map(week => (
                <GeneralButton
                  key={week}
                  type="button"
                  buttonType="secondary"
                  onClick={() => navigate(`/orders/create?customer_id=${customerId}&default_order=${week}`)}
                >
                  {formData[`default_order_${week}_id`] ? `Edit Week ${week} Order` : `Set Week ${week} Order`}
                </GeneralButton>
              ))}
            </div>
          )}
        </form>
      )}
    </MasterLayout>
  );
}

export default CustomerForm;
